<template>
  <Dialog
    v-model:visible="visible"
    modal
    header="Исключения состояния"
    :style="{ width: '70vw', height: '80vh' }"
    :maximizable="true"
    @update:visible="onDialogClose"
    class="condition-exceptions-dialog"
  >
    <div class="container">
      <div class="toolbar mb-3">
        <div class="flex justify-content-between align-items-start gap-3 flex-wrap">
          <div>
            <h2 class="text-900 mb-1">Принудительные значения колонки «Состояние»</h2>
            <p class="text-600 mt-1 mb-0">
              Перебивают автоматический пересчёт (demo-фид и site_status).
              Правило для бренда важнее правила производителя.
            </p>
          </div>
          <div class="flex gap-2">
            <Button
              label="Пересчитать сейчас"
              icon="pi pi-refresh"
              severity="secondary"
              :loading="isApplying"
              @click="applyAll"
              v-tooltip.bottom="'Запустить полный пересчёт состояния (учтёт все правила)'"
            />
            <Button
              label="Добавить исключение"
              icon="pi pi-plus"
              @click="openCreateDialog"
            />
          </div>
        </div>
      </div>

      <DataTable
        :value="exceptions"
        :loading="isLoading"
        :paginator="false"
        class="p-datatable-sm"
        scrollable
        scrollHeight="flex"
        emptyMessage="Исключения не заданы"
      >
        <Column header="Тип" style="width: 180px">
          <template #body="slotProps">
            <Tag
              :value="scopeLabel(slotProps.data.scope)"
              :severity="slotProps.data.scope === 'producer' ? 'info' : 'success'"
            />
          </template>
        </Column>

        <Column field="target_name" header="Объект">
          <template #body="slotProps">
            <div>
              <div class="font-semibold">{{ slotProps.data.target_name }}</div>
              <div v-if="slotProps.data.scope === 'producer'" class="text-xs text-500">
                Бренды: {{ (slotProps.data.affected_brands || []).length }}
                <span v-if="(slotProps.data.affected_brands || []).length === 0">
                  (нет привязанных брендов)
                </span>
              </div>
            </div>
          </template>
        </Column>

        <Column field="condition_value" header="Состояние" style="width: 130px">
          <template #body="slotProps">
            <Tag
              v-if="slotProps.data.condition_value != null && slotProps.data.condition_value !== ''"
              :value="slotProps.data.condition_value"
              severity="warning"
            />
            <span v-else class="text-500">—</span>
          </template>
        </Column>

        <Column field="site_status_value" header="Статус на сайте" style="min-width: 180px">
          <template #body="slotProps">
            <Tag
              v-if="slotProps.data.site_status_value != null && slotProps.data.site_status_value !== ''"
              :value="slotProps.data.site_status_value"
              severity="info"
            />
            <span v-else class="text-500">—</span>
          </template>
        </Column>

        <Column header="Действия" style="width: 140px">
          <template #body="slotProps">
            <div class="flex gap-1">
              <Button
                icon="pi pi-pencil"
                size="small"
                severity="secondary"
                @click="openEditDialog(slotProps.data)"
                v-tooltip.bottom="'Редактировать'"
              />
              <Button
                icon="pi pi-trash"
                size="small"
                severity="danger"
                @click="confirmDelete(slotProps.data)"
                v-tooltip.bottom="'Удалить'"
              />
            </div>
          </template>
        </Column>
      </DataTable>
    </div>

    <template #footer>
      <Button label="Закрыть" icon="pi pi-times" severity="secondary" @click="closeDialog" />
    </template>
  </Dialog>

  <!-- Add/Edit Dialog -->
  <Dialog
    v-model:visible="editorVisible"
    modal
    :header="editing ? 'Изменить исключение' : 'Новое исключение'"
    :style="{ width: '40rem' }"
    :closable="!isSaving"
  >
    <div class="p-fluid editor">
      <div class="field mb-3">
        <label class="block text-900 font-medium mb-2">Тип объекта</label>
        <SelectButton
          v-model="form.scope"
          :options="scopeOptions"
          optionLabel="label"
          optionValue="value"
          :allowEmpty="false"
          @change="onScopeChange"
        />
        <small class="text-500 block mt-2">
          Производитель применяет правило ко всем своим брендам сразу.
        </small>
      </div>

      <div class="field mb-3">
        <label class="block text-900 font-medium mb-2">
          {{ form.scope === 'producer' ? 'Производитель' : 'Бренд' }}
        </label>
        <Dropdown
          v-model="form.target_name"
          :options="form.scope === 'producer' ? producerOptions : brandOptions"
          :placeholder="form.scope === 'producer' ? 'Выберите производителя' : 'Выберите бренд'"
          filter
          :showClear="true"
          class="w-full"
        />
        <small v-if="form.scope === 'producer' && previewBrands.length" class="text-500 block mt-2">
          Будет применено к {{ previewBrands.length }} брендам:
        </small>
        <div v-if="form.scope === 'producer' && previewBrands.length" class="brand-chips mt-2">
          <Chip
            v-for="b in previewBrands"
            :key="b"
            :label="b"
            class="mr-1 mb-1"
          />
        </div>
        <small
          v-if="form.scope === 'producer' && form.target_name && previewBrands.length === 0"
          class="text-orange-500 block mt-2"
        >
          У этого производителя нет привязанных брендов в справочнике.
        </small>
      </div>

      <div class="field mb-3">
        <label class="block text-900 font-medium mb-2">Значение колонки «Состояние»</label>
        <InputText
          v-model="form.condition_value"
          placeholder="0, 1, 2, 3, 4 или 5 (оставьте пустым — не менять)"
          class="w-full"
        />
        <small class="text-500 block mt-2">
          Допустимы значения 0–5 (см. словарь site_status). Вводится вручную.
        </small>
      </div>

      <div class="field mb-3">
        <label class="block text-900 font-medium mb-2">Значение колонки «Статус на сайте»</label>
        <InputText
          v-model="form.site_status_value"
          placeholder="Например: Pre-Order, Limited (оставьте пустым — не менять)"
          class="w-full"
        />
        <small class="text-500 block mt-2">
          Произвольная строка. Будет принудительно записана в site_status у всех товаров бренда/производителя.
        </small>
      </div>

      <div v-if="editorError" class="p-error text-sm mb-2">
        {{ editorError }}
      </div>

      <div class="hint text-xs text-500">
        <i class="pi pi-info-circle mr-1"></i>
        Хотя бы одно из двух полей должно быть задано. Если у бренда есть собственное правило —
        оно важнее правила производителя, в который этот бренд входит. Поля condition и site_status
        накладываются независимо.
      </div>
    </div>

    <template #footer>
      <Button
        label="Отмена"
        icon="pi pi-times"
        severity="secondary"
        :disabled="isSaving"
        @click="editorVisible = false"
      />
      <Button
        :label="editing ? 'Сохранить' : 'Создать'"
        icon="pi pi-check"
        :loading="isSaving"
        :disabled="!canSubmit"
        @click="submit"
      />
    </template>
  </Dialog>
</template>

<script>
import { ref, computed, watch } from 'vue'
import { useToast } from 'primevue/usetoast'
import { useConfirm } from 'primevue/useconfirm'
import Dialog from 'primevue/dialog'
import DataTable from 'primevue/datatable'
import Column from 'primevue/column'
import Button from 'primevue/button'
import InputText from 'primevue/inputtext'
import Dropdown from 'primevue/dropdown'
import SelectButton from 'primevue/selectbutton'
import Tag from 'primevue/tag'
import Chip from 'primevue/chip'
import Tooltip from 'primevue/tooltip'
import apiService from '@/services/apiService'

export default {
  name: 'ConditionExceptionsDialog',
  components: {
    Dialog,
    DataTable,
    Column,
    Button,
    InputText,
    Dropdown,
    SelectButton,
    Tag,
    Chip,
  },
  directives: { tooltip: Tooltip },
  props: {
    modelValue: { type: Boolean, default: false },
  },
  emits: ['update:modelValue'],
  setup(props, { emit }) {
    const toast = useToast()
    const confirm = useConfirm()

    const visible = ref(props.modelValue)
    const exceptions = ref([])
    const isLoading = ref(false)
    const isApplying = ref(false)

    const editorVisible = ref(false)
    const isSaving = ref(false)
    const editing = ref(null) // null = create, иначе объект правила
    const editorError = ref('')

    const form = ref({
      scope: 'brand',
      target_name: null,
      condition_value: '',
      site_status_value: '',
    })

    // Опции
    const brandOptions = ref([])      // ['GUCCI', 'RAY-BAN', ...]
    const producerOptions = ref([])   // ['LUXOTTICA', ...]
    const producerToBrands = ref({})  // { 'LUXOTTICA': ['RAY-BAN', ...] }

    const scopeOptions = [
      { label: 'Бренд', value: 'brand' },
      { label: 'Производитель', value: 'producer' },
    ]

    const scopeLabel = (scope) =>
      scope === 'producer' ? 'Производитель' : 'Бренд'

    const previewBrands = computed(() => {
      if (form.value.scope !== 'producer' || !form.value.target_name) return []
      return producerToBrands.value[form.value.target_name] || []
    })

    const canSubmit = computed(() => {
      const hasCond = !!(form.value.condition_value || '').trim()
      const hasSite = !!(form.value.site_status_value || '').trim()
      return (
        !!form.value.target_name &&
        (hasCond || hasSite) &&
        !isSaving.value
      )
    })

    // ---------- Загрузка данных ----------
    const loadAll = async () => {
      isLoading.value = true
      try {
        const [rules, filterOpts, bpMap] = await Promise.all([
          apiService.getConditionExceptions(),
          apiService.getFilterOptions(),
          apiService.getBrandProducers(),
        ])
        exceptions.value = rules || []

        // Бренды — distinct из products
        const brandsFromProducts = (filterOpts?.data?.brands || []).map(String)
        // Доп. бренды из brand_producer_map (могут быть бренды без товаров)
        const brandsFromMap = (bpMap || []).map((m) => String(m.brand || ''))
        const brandsSet = new Set(
          [...brandsFromProducts, ...brandsFromMap]
            .map((b) => b.trim().toUpperCase())
            .filter(Boolean)
        )
        brandOptions.value = [...brandsSet].sort()

        // Производители + карта producer -> [brands]
        const map = {}
        for (const item of bpMap || []) {
          const p = String(item.producer || '').trim().toUpperCase()
          const b = String(item.brand || '').trim().toUpperCase()
          if (!p || !b) continue
          if (!map[p]) map[p] = []
          if (!map[p].includes(b)) map[p].push(b)
        }
        for (const p of Object.keys(map)) map[p].sort()
        producerToBrands.value = map
        producerOptions.value = Object.keys(map).sort()
      } catch (e) {
        toast.add({
          severity: 'error',
          summary: 'Ошибка',
          detail: 'Не удалось загрузить данные',
          life: 5000,
        })
      } finally {
        isLoading.value = false
      }
    }

    const loadRules = async () => {
      try {
        exceptions.value = await apiService.getConditionExceptions()
      } catch (e) {
        toast.add({
          severity: 'error',
          summary: 'Ошибка',
          detail: 'Не удалось обновить список правил',
          life: 4000,
        })
      }
    }

    // ---------- Add / Edit ----------
    const resetForm = () => {
      form.value = {
        scope: 'brand',
        target_name: null,
        condition_value: '',
        site_status_value: '',
      }
      editorError.value = ''
    }

    const openCreateDialog = () => {
      editing.value = null
      resetForm()
      editorVisible.value = true
    }

    const openEditDialog = (rule) => {
      editing.value = rule
      form.value = {
        scope: rule.scope,
        target_name: rule.target_name,
        condition_value: rule.condition_value || '',
        site_status_value: rule.site_status_value || '',
      }
      editorError.value = ''
      editorVisible.value = true
    }

    // При смене типа объект сбрасывается — нельзя оставить производителя,
    // переключив тип на «Бренд», иначе target_name не валиден.
    const onScopeChange = () => {
      form.value.target_name = null
      editorError.value = ''
    }

    const submit = async () => {
      if (!canSubmit.value) return
      const cond = (form.value.condition_value || '').trim()
      const site = (form.value.site_status_value || '').trim()
      const payload = {
        scope: form.value.scope,
        target_name: String(form.value.target_name || '').trim().toUpperCase(),
        condition_value: cond || null,
        site_status_value: site || null,
      }
      isSaving.value = true
      editorError.value = ''
      try {
        if (editing.value) {
          await apiService.updateConditionException(editing.value.id, payload)
          toast.add({
            severity: 'success',
            summary: 'Сохранено',
            detail: 'Правило обновлено. Состояние пересчитывается в фоне.',
            life: 4000,
          })
        } else {
          await apiService.createConditionException(payload)
          toast.add({
            severity: 'success',
            summary: 'Добавлено',
            detail: 'Правило применено к товарам.',
            life: 4000,
          })
        }
        editorVisible.value = false
        await loadRules()
      } catch (e) {
        editorError.value =
          e?.response?.data?.detail || 'Не удалось сохранить правило'
      } finally {
        isSaving.value = false
      }
    }

    const confirmDelete = (rule) => {
      confirm.require({
        message: `Удалить исключение для ${scopeLabel(rule.scope).toLowerCase()} «${rule.target_name}»?`,
        header: 'Подтверждение',
        icon: 'pi pi-exclamation-triangle',
        acceptLabel: 'Удалить',
        rejectLabel: 'Отмена',
        acceptClass: 'p-button-danger',
        accept: async () => {
          try {
            await apiService.deleteConditionException(rule.id)
            toast.add({
              severity: 'success',
              summary: 'Удалено',
              detail: 'Запущен пересчёт состояния (обновится в течение пары минут).',
              life: 4000,
            })
            await loadRules()
          } catch (e) {
            toast.add({
              severity: 'error',
              summary: 'Ошибка',
              detail: e?.response?.data?.detail || 'Не удалось удалить правило',
              life: 5000,
            })
          }
        },
      })
    }

    const applyAll = async () => {
      isApplying.value = true
      try {
        await apiService.applyConditionExceptions()
        toast.add({
          severity: 'success',
          summary: 'Запущено',
          detail: 'Пересчёт состояния идёт в фоне.',
          life: 4000,
        })
      } catch (e) {
        toast.add({
          severity: e?.response?.status === 409 ? 'warn' : 'error',
          summary: e?.response?.status === 409 ? 'Уже выполняется' : 'Ошибка',
          detail:
            e?.response?.data?.detail || 'Не удалось запустить пересчёт состояния',
          life: 5000,
        })
      } finally {
        isApplying.value = false
      }
    }

    // ---------- Dialog visibility ----------
    const closeDialog = () => {
      visible.value = false
    }

    const onDialogClose = (value) => {
      visible.value = value
      emit('update:modelValue', value)
    }

    watch(
      () => props.modelValue,
      (v) => {
        visible.value = v
        if (v) loadAll()
      }
    )

    return {
      visible,
      exceptions,
      isLoading,
      isApplying,
      editorVisible,
      isSaving,
      editing,
      editorError,
      form,
      brandOptions,
      producerOptions,
      scopeOptions,
      scopeLabel,
      previewBrands,
      canSubmit,
      openCreateDialog,
      openEditDialog,
      onScopeChange,
      submit,
      confirmDelete,
      applyAll,
      closeDialog,
      onDialogClose,
    }
  },
}
</script>

<style scoped>
.container {
  height: 100%;
  display: flex;
  flex-direction: column;
}

.toolbar h2 {
  margin: 0;
  font-size: 1.1rem;
}

.brand-chips {
  max-height: 120px;
  overflow-y: auto;
  padding: 4px;
  border: 1px solid var(--surface-border);
  border-radius: 4px;
  background: var(--surface-50);
}

.editor .field {
  display: flex;
  flex-direction: column;
}

.hint {
  padding: 8px;
  background: var(--surface-100);
  border-radius: 4px;
}
</style>